import styles from './FJSourcePreviewItem.module.less';
import { memo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import FJSourcePreviewItemBtu from './FJSourcePreviewItemBtu';
import FJSourceReviewItemStatus from './FJSourceReviewItemStatus';
import FJVideoPreviewItem from './FJVideoPreviewItem';
import FJConstant from '../../../constants/FJConstant';
import { useMemo } from 'react';
import FJAppConstant from '../../../constants/FJAppConstant';
import FJImagePreviewItem from './FJImagePreviewItem';

const FJSourcePreviewItem = memo(props => {
    const {
        item,
        appType,

        selected = false,
        isShowSelected = false,
        hideSelect = false,
        hideDelete = false,
        hideCompare = true,
        hideDownload = false,
        hideEdit = true,
        isTransparentBG = false,
        isShowAddToMediaButton = false,
        isApplyButton = false,
        isShowAddToEditPageButton = false,

        onAddToMedia,
        onApplyMedia,
        onSelected,
        onDelete,
        onDownload,
        reGenerate,
        onClick,
        onEdit,
        onReload,
        onAddToEditPage,
    } = props;

    const { imgUrl, id, width, height, isLoading, isReload, originalUrl, videoUrl, status } = item;

    const [sourceLoadStatus, setSourceLoadStatus] = useState(FJConstant.LOADING);
    const [sourceOriginLoadStatus, setSourceOriginLoadStatus] = useState(FJConstant.LOADING);
    const [isHover, setIsHover] = useState(false);
    const [isComparison, setIsComparison] = useState(false);
    const videoPreviewItemRef = useRef(null);
    const imagePreviewItemRef = useRef(null);

    const _onClick = e => {
        e.stopPropagation();
        if (sourceLoadStatus === FJConstant.LOADING || sourceLoadStatus === FJConstant.ERROR || isLoading || isReload)
            return;
        onClick && onClick(item);
    };

    // FJSourceReviewItemStatus 状态
    const sourceStatus = useMemo(() => {
        if (status === 'progress') {
            return FJConstant.PROGRESS;
        }

        if (status === 'failed') {
            return FJConstant.ERROR;
        }

        if (isReload) {
            return FJConstant.RETRY;
        }

        if (isLoading) {
            return FJConstant.LOADING;
        }

        // 资源加载失败状态和 isReload  status === 'failed' 区分开
        if (sourceLoadStatus === FJConstant.ERROR) {
            return FJConstant.SOURCE_LOADING_ERROR;
        }

        if (sourceLoadStatus === FJConstant.LOADING) {
            return FJConstant.LOADING;
        }

        return sourceLoadStatus;
    }, [status, sourceLoadStatus, isReload, isLoading]);

    // 状态界面需要的特殊数据
    const statusData = useMemo(() => {
        if (appType === FJAppConstant.AI_VIDEO_GENERATOR) {
            return {
                generationType: item.generationType,
                originalUrl: item.originalUrl,
            };
        }
    }, [item, appType]);

    const _onReloadOrigin = () => {
        videoPreviewItemRef.current?.sourceOriginReload();
    };

    const _reGenerate = () => {
        reGenerate(id);
    };

    const _onSourceReload = () => {
        if (videoUrl) {
            videoPreviewItemRef.current?.sourceReload();
        } else {
            imagePreviewItemRef.current?.sourceReload();
        }
        onReload && onReload(id);
    };

    const _onApplyMedia = e => {
        e.stopPropagation();
        let options;
        if (width && height) {
            options = {
                size: { width: width, height: height },
            };
        }
        onApplyMedia && onApplyMedia(imgUrl, options);
    };

    // 添加到编辑页的媒体库
    const _onAddToMediaLibrary = e => {
        e.stopPropagation();
        onAddToMedia && onAddToMedia(imgUrl);
    };

    const _onAddToEditPage = (e, callback) => {
        e.stopPropagation();
        onAddToEditPage && onAddToEditPage(id, callback);
    };

    return (
        <div
            className={classNames(styles.box, isShowSelected ? styles.selected : '')}
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            onClick={_onClick}
        >
            {/* 资源预览 */}
            {videoUrl ? (
                <FJVideoPreviewItem
                    ref={videoPreviewItemRef}
                    videoUrl={videoUrl}
                    thumbnailUrl={imgUrl}
                    isLoading={sourceStatus === FJConstant.LOADING}
                    sourceLoadStatus={sourceLoadStatus}
                    setSourceLoadStatus={setSourceLoadStatus}
                    isHover={isHover}
                    isTransparentBG={isTransparentBG}
                />
            ) : (
                <FJImagePreviewItem
                    ref={imagePreviewItemRef}
                    imgUrl={imgUrl}
                    isLoading={sourceStatus === FJConstant.LOADING}
                    originalUrl={originalUrl}
                    sourceLoadStatus={sourceLoadStatus}
                    setSourceLoadStatus={setSourceLoadStatus}
                    setSourceOriginLoadStatus={setSourceOriginLoadStatus}
                    isTransparentBG={isTransparentBG}
                    isComparison={isComparison}
                />
            )}

            {/* 操作按钮 */}
            <FJSourcePreviewItemBtu
                loadStatus={sourceStatus}
                sourceLoadStatus={sourceLoadStatus}
                sourceOriginLoadStatus={sourceOriginLoadStatus}
                isShowSelected={isShowSelected}
                propsIsHover={isHover}
                selected={selected}
                hideSelect={hideSelect}
                hideDelete={hideDelete}
                hideCompare={hideCompare}
                hideDownload={hideDownload}
                hideEdit={hideEdit}
                isShowAddToMediaButton={isShowAddToMediaButton}
                isApplyButton={isApplyButton}
                isShowAddToEditPageButton={isShowAddToEditPageButton}
                onAddToEditPage={_onAddToEditPage}
                onApplyMedia={isApplyButton ? _onApplyMedia : _onAddToMediaLibrary}
                onSelected={(bool, e) => {
                    onSelected && onSelected(id, bool, e);
                }}
                onDelete={() => {
                    onDelete && onDelete(id);
                }}
                onDownload={callback => {
                    onDownload && onDownload(id, callback);
                }}
                onEdit={callback => {
                    onEdit && onEdit(item, callback);
                }}
                isComparison={isComparison}
                setIsComparison={setIsComparison}
                onReloadOrigin={_onReloadOrigin}
            />

            {/* 状态 */}
            <FJSourceReviewItemStatus
                status={sourceStatus}
                data={statusData}
                onSourceReload={_onSourceReload}
                onRetry={_reGenerate}
            />
        </div>
    );
});

FJSourcePreviewItem.propTypes = {
    item: PropTypes.object.isRequired,
    selected: PropTypes.bool,
    isShowSelected: PropTypes.bool,
    onSelected: PropTypes.func,
    onDelete: PropTypes.func,
    onDownload: PropTypes.func,
    onEdit: PropTypes.func,
    reGenerate: PropTypes.func,
    onClick: PropTypes.func,
    hideSelect: PropTypes.bool,
    hideDelete: PropTypes.bool,
    hideCompare: PropTypes.bool,
    hideDownload: PropTypes.bool,
    hideEdit: PropTypes.bool,
    appType: PropTypes.string,
    isTransparentBG: PropTypes.bool,
    isShowAddToMediaButton: PropTypes.bool,
    isApplyButton: PropTypes.bool,
    onApplyMedia: PropTypes.func,
    onAddToMedia: PropTypes.func,
    onReload: PropTypes.func,
    onAddToEditPage: PropTypes.func,
    isShowAddToEditPageButton: PropTypes.bool,
};

FJSourcePreviewItem.displayName = 'FJSourcePreviewItem';
export default FJSourcePreviewItem;
